/**
 * /platform/video/:id — один ролик (ч.1319).
 *
 * ч.1327: ролик буває до сторінки, сценарієм через кілька сторінок (завдання редактора — тут же,
 * можна змінити й перезняти) або відповіддю на часте питання довідки.
 *
 * Редактор дивиться ролик, пише зауваження («говорить швидко», «покажи ще кнопку друк») —
 * їх виконує агент: правит сценарій, за потреби темп, і перезнімає. Текст диктора можна
 * поправити й руками. «Схвалити» публікує на YouTube (якщо канал підключено). Поки агент
 * працює, сторінка оновлюється сама, а дії недоступні — з поясненням чому.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ACTION_LABEL,
  BUSY_STATUSES,
  KIND_LABEL,
  fmtDuration,
  platformVideo,
  targetLabel,
  type ArticleCoverage,
  type GuideAction,
  type VideoGuide,
  type VideoScript,
  type DemoMode,
} from '@/api/platformVideo';
import { extractApiError } from '@/utils/extractApiError';
import { DemoModeField, DevRequestForm, NOTICE, VideoStatusBadge, fmtDT, useCanOpenHelpArticles } from './PlatformVideoPage';
import TchTable from '@/shared/components/TchTable';
import SearchSelect from '@/components/SearchSelect';

const JOB_LABEL: Record<string, string> = {
  SCRIPT: 'сценарій і зйомка', RENDER: 'зйомка', REVISE: 'зауваження', PUBLISH: 'публікація',
  VALIDATE: 'нічна перевірка',
};
const JOB_STATUS: Record<string, string> = {
  PENDING: 'у черзі', RUNNING: 'виконується', DONE: 'готово', FAILED: 'помилка', CANCELLED: 'скасовано',
};
const REMARK_HINTS = [
  'Говорить занадто швидко',
  'Покажи ще, де кнопка «друк»',
  'Прибери крок про фільтри — він зайвий',
  'Наприкінці скажи, як провести документ',
];

function busyOf(g: VideoGuide | undefined): boolean {
  if (!g) return false;
  return BUSY_STATUSES.includes(g.status)
    || g.jobs.some((j) => (j.status === 'PENDING' || j.status === 'RUNNING') && j.kind !== 'VALIDATE');
}

function Player({ guide }: { guide: VideoGuide }) {
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const stamp = `${guide.id}:${guide.script_version}:${guide.rendered_at ?? ''}`;
  useEffect(() => {
    if (!guide.has_video) { setUrl(null); return undefined; }
    let alive = true;
    let made: string | null = null;
    platformVideo.fileUrl(guide.id, 'mp4')
      .then((u) => { made = u; if (alive) { setUrl(u); setErr(null); } else URL.revokeObjectURL(u); })
      .catch((e) => { if (alive) setErr(extractApiError(e, 'Не вдалося завантажити ролик')); });
    return () => { alive = false; if (made) URL.revokeObjectURL(made); };
  }, [stamp, guide.has_video, guide.id]);
  if (!guide.has_video) {
    return (
      <div style={{ aspectRatio: '16 / 9', display: 'grid', placeItems: 'center', background: 'var(--bg-soft)',
        border: '1px dashed var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)' }}>
        {busyOf(guide) ? 'Агент працює над роликом — сторінка оновиться сама' : 'Ролика ще немає'}
      </div>
    );
  }
  if (err) return <div className="error">{err}</div>;
  if (!url) return <p>Завантаження ролика…</p>;
  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption -- субтитри є окремим файлом (.srt)
    <video data-testid="video-player" src={url} controls preload="metadata"
      style={{ width: '100%', borderRadius: 'var(--radius-sm)', background: '#000' }} />
  );
}

async function download(id: string, kind: 'mp4' | 'srt', name: string) {
  const u = await platformVideo.fileUrl(id, kind);
  const a = document.createElement('a');
  a.href = u; a.download = name; a.click();
  window.setTimeout(() => URL.revokeObjectURL(u), 10_000);
}

function ScriptBlock({ guide, busy }: { guide: VideoGuide; busy: boolean }) {
  const qc = useQueryClient();
  const [edit, setEdit] = useState<VideoScript | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (s: VideoScript) => platformVideo.saveScript(guide.id, s),
    onSuccess: () => { setEdit(null); setErr(null); qc.invalidateQueries({ queryKey: ['platform-video-guide', guide.id] }); },
    onError: (e) => setErr(extractApiError(e)),
  });
  const script = guide.script;
  if (!script) return <p style={{ color: 'var(--text-secondary)' }}>Сценарію ще немає.</p>;
  const shown = edit ?? script;
  const setStep = (i: number, narration: string) => setEdit((s) => (s ? {
    ...s, steps: s.steps.map((st, k) => (k === i ? { ...st, narration } : st)),
  } : s));
  return (
    <div>
      {edit ? (
        <>
          <div className="field">
            <label htmlFor="vg-title">Назва ролика</label>
            <input id="vg-title" value={edit.title} maxLength={200}
              onChange={(e) => setEdit({ ...edit, title: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="vg-desc">Опис на YouTube</label>
            <textarea id="vg-desc" rows={3} value={edit.description} maxLength={4000}
              onChange={(e) => setEdit({ ...edit, description: e.target.value })} />
          </div>
        </>
      ) : (
        <p style={{ margin: '0 0 0.5rem', fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>{script.description}</p>
      )}
      <TchTable style={{ width: '100%' }}>
        <thead>
          <tr>
            <th style={{ width: '2.5rem' }}>№</th>
            <th style={{ width: '14rem' }}>На екрані</th>
            <th>Каже диктор</th>
          </tr>
        </thead>
        <tbody>
          {shown.steps.map((st, i) => (
            // eslint-disable-next-line react/no-array-index-key -- кроки не мають власного ключа
            <tr key={i}>
              <td>{i + 1}</td>
              <td style={{ fontSize: '0.8rem' }}>
                <b>{ACTION_LABEL[st.action]}</b>{st.target ? `: ${targetLabel(st.target)}` : ''}
                {st.value ? <span style={{ color: 'var(--text-secondary)' }}> — {st.value}</span> : null}
              </td>
              <td>
                {edit ? (
                  <textarea aria-label={`Текст кроку ${i + 1}`} rows={2} value={st.narration} maxLength={1200}
                    onChange={(e) => setStep(i, e.target.value)} style={{ width: '100%' }} />
                ) : st.narration}
              </td>
            </tr>
          ))}
        </tbody>
      </TchTable>
      {err && <div className="error">{err}</div>}
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
        {edit ? (
          <>
            <button type="button" className="primary" disabled={save.isPending || busy}
              onClick={() => save.mutate(edit)}>
              {save.isPending ? 'зберігаємо…' : 'зберегти і перезняти'}
            </button>
            <button type="button" onClick={() => { setEdit(null); setErr(null); }}>скасувати</button>
          </>
        ) : (
          <button type="button" disabled={busy}
            title={busy ? 'Агент зараз працює над роликом — дочекайтесь' : 'Поправити текст диктора і назву вручну'}
            onClick={() => setEdit(structuredClone(script))}>
            правити текст вручну
          </button>
        )}
      </div>
    </div>
  );
}

/** ч.1327: завдання ролика-сценарію — показ і правка (переписати сценарій — за бажанням одразу).
 *  ч.1362: і ролика до сторінки — там воно необовʼязкове (без нього агент іде за статтею). */
function BriefBlock({ guide, busy, onSaved }: { guide: VideoGuide; busy: boolean; onSaved: (m: string) => void }) {
  const optional = guide.kind === 'PAGE';
  const minLen = optional ? 0 : 10;
  const qc = useQueryClient();
  const [edit, setEdit] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: async (again: boolean) => {
      await platformVideo.patchGuide(guide.id, { brief: (edit ?? '').trim() });
      if (again) await platformVideo.action(guide.id, 'regenerate');
      return again;
    },
    onSuccess: (again) => {
      setEdit(null); setErr(null);
      onSaved(again ? 'Завдання збережено — агент проходить сценарій заново.' : 'Завдання збережено.');
      qc.invalidateQueries({ queryKey: ['platform-video-guide', guide.id] });
      qc.invalidateQueries({ queryKey: ['platform-video-topics'] });
    },
    onError: (e) => setErr(extractApiError(e)),
  });
  const tip = busy ? 'Агент зараз працює над роликом — дочекайтесь' : undefined;
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <h3 style={{ margin: '0 0 0.35rem' }}>{optional ? 'Завдання агенту' : 'Завдання сценарію'}</h3>
      {edit === null ? (
        <>
          <p data-testid="video-brief" style={{ margin: '0 0 0.5rem', whiteSpace: 'pre-wrap',
            color: guide.brief ? undefined : 'var(--text-secondary)' }}>
            {guide.brief || 'Немає — агент проходить статтю довідки сторінки розділ за розділом.'}
          </p>
          <button type="button" disabled={busy} title={tip} onClick={() => setEdit(guide.brief ?? '')}>
            {guide.brief || !optional ? 'змінити завдання' : 'дати завдання'}
          </button>
        </>
      ) : (
        <>
          <textarea aria-label={optional ? 'Завдання агенту' : 'Завдання сценарію'} rows={4} maxLength={4000}
            value={edit} onChange={(e) => setEdit(e.target.value)} style={{ width: '100%' }}
            placeholder={optional ? 'Наприклад: покажи обидві вкладки рядків, заповни і проведи документ, наприкінці — друк.' : undefined} />
          {optional && <div className="cell-hint">Порожнє — знімає завдання: агент іде за статтею.</div>}
          {err && <div className="error">{err}</div>}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.35rem' }}>
            <button type="button" className="primary" disabled={busy || edit.trim().length < minLen || save.isPending}
              title={tip ?? 'Зберегти й одразу переписати сценарій за новим завданням'}
              onClick={() => save.mutate(true)}>
              зберегти і зняти заново
            </button>
            <button type="button" disabled={busy || edit.trim().length < minLen || save.isPending} title={tip}
              onClick={() => save.mutate(false)}>
              зберегти
            </button>
            <button type="button" onClick={() => { setEdit(null); setErr(null); }}>скасувати</button>
          </div>
        </>
      )}
    </div>
  );
}

/** ч.1413: режим демо-акаунта ролика. Готовий ролик знято в іншому режимі — лише зберегти мало. */
function DemoModeBlock({ guide, busy, onSaved }: { guide: VideoGuide; busy: boolean; onSaved: (m: string) => void }) {
  const saved = (guide.settings_override?.demo_mode as DemoMode | undefined) ?? 'OWNER';
  const qc = useQueryClient();
  const [edit, setEdit] = useState<DemoMode | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: async (again: boolean) => {
      await platformVideo.patchGuide(guide.id, { demo_mode: edit! });
      if (again) await platformVideo.action(guide.id, 'regenerate');
      return again;
    },
    onSuccess: (again) => {
      setEdit(null); setErr(null);
      onSaved(again ? 'Режим збережено — агент проходить ролик заново.'
        : 'Режим збережено. Він діятиме при наступній зйомці.');
      qc.invalidateQueries({ queryKey: ['platform-video-guide', guide.id] });
    },
    onError: (e) => setErr(extractApiError(e)),
  });
  const tip = busy ? 'Агент зараз працює над роликом — дочекайтесь' : undefined;
  const value = edit ?? saved;
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <DemoModeField id="video-guide-mode" value={value} disabled={busy}
        onChange={(m) => { setEdit(m === saved ? null : m); setErr(null); }} />
      {err && <div className="error">{err}</div>}
      {edit && (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button type="button" className="primary" disabled={busy || save.isPending} title={tip}
            onClick={() => save.mutate(true)}>зберегти і зняти заново</button>
          <button type="button" disabled={busy || save.isPending} title={tip}
            onClick={() => save.mutate(false)}>зберегти</button>
          <button type="button" onClick={() => setEdit(null)}>скасувати</button>
        </div>
      )}
    </div>
  );
}

/** ч.1362: які розділи статті сторінки показано в ролику, а яких бракує (та сама міра, що в агента). */
function CoverageBlock({ coverage }: { coverage: ArticleCoverage }) {
  const gaps = coverage.sections.filter((x) => !x.covered);
  const open = gaps.length > 0 || coverage.posted === false;
  return (
    <details data-testid="video-coverage" open={open} style={{ marginBottom: '0.75rem' }}>
      <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
        Покриття статті: {coverage.covered} з {coverage.total} розділів
        {gaps.length ? <span style={{ fontWeight: 400, color: 'var(--badge-amber-fg)' }}> · не показано {gaps.length}</span> : null}
      </summary>
      {coverage.posted === false && (
        <div data-testid="video-not-posted" style={{ margin: '0.35rem 0 0', fontSize: '0.875rem', color: 'var(--badge-amber-fg)' }}>
          Основний сценарій не показано до кінця: документ у ролику не проведено.
        </div>
      )}
      <ul style={{ margin: '0.35rem 0 0', paddingLeft: '1.2rem', fontSize: '0.875rem' }}>
        {coverage.sections.map((x) => (
          <li key={x.title} style={{ marginLeft: x.level > 2 ? '1rem' : 0,
            color: x.covered ? 'var(--text-secondary)' : undefined }}>
            {x.covered ? '✓ ' : '— '}{x.title}
            {!x.covered && x.missing.length > 0 && (
              <span style={{ color: 'var(--text-secondary)' }}> · бракує: {x.missing.slice(0, 6).map((m) => `«${m}»`).join(', ')}</span>
            )}
          </li>
        ))}
      </ul>
      <div className="cell-hint" style={{ marginTop: '0.35rem' }}>
        Розділ показано, якщо в ролику названо хоча б третину його полів і кнопок. Щоб доробити — зауваження
        агенту («покажи розділ…») або «написати сценарій заново».
      </div>
    </details>
  );
}

/** ч.1331: довідка, за якою знято ролик, змінилась — що саме, «ролик актуальний», перепривʼязка питання. */
function HelpChanged({ guide, busy, onDone }: { guide: VideoGuide; busy: boolean; onDone: (m: string) => void }) {
  const qc = useQueryClient();
  const orphan = guide.kind === 'FAQ' && !guide.article;
  const [slug, setSlug] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const questions = useQuery({
    queryKey: ['platform-video-faq'], queryFn: platformVideo.faqQuestions, enabled: guide.kind === 'FAQ',
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['platform-video-guide', guide.id] });
    qc.invalidateQueries({ queryKey: ['platform-video-faq'] });
    qc.invalidateQueries({ queryKey: ['platform-video-help'] });
  };
  const accept = useMutation({
    mutationFn: () => platformVideo.action(guide.id, 'help_ok'),
    onSuccess: () => { setErr(null); onDone('Позначку знято: ролик актуальний.'); refresh(); },
    onError: (e) => setErr(extractApiError(e)),
  });
  const relink = useMutation({
    mutationFn: () => platformVideo.relink(guide.id, slug),
    onSuccess: () => { setErr(null); setSlug(''); onDone('Ролик перепривʼязано до питання.'); refresh(); },
    onError: (e) => setErr(extractApiError(e)),
  });
  if (!guide.help_change_note && !orphan) return null;
  const free = (questions.data?.items ?? []).filter((q) => !q.guide_id)
    .map((q) => ({ id: q.slug, label: q.title.replace(/^—\s*/, ''), hint: q.section }));
  const tip = busy ? 'Агент зараз працює над роликом — дочекайтесь' : undefined;
  return (
    <div data-testid="video-help-banner" style={{ background: 'var(--badge-amber-bg)', color: 'var(--badge-amber-fg)',
      border: '1px solid var(--badge-amber-bd)', borderRadius: 'var(--radius-sm)', padding: '0.5rem 0.75rem',
      fontSize: '0.875rem', marginBottom: '0.75rem' }}>
      <b>Довідка змінилась.</b> {guide.help_change_note ?? 'Питання цього ролика в довідці немає.'}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginTop: '0.4rem' }}>
        {!orphan && (
          <button type="button" disabled={busy || accept.isPending} title={tip ?? 'Ролик переглянуто — він відповідає довідці'}
            onClick={() => accept.mutate()}>
            ролик актуальний
          </button>
        )}
        <span style={{ color: 'var(--text-secondary)' }}>
          {orphan ? 'Оберіть питання, до якого привʼязати ролик, або видаліть ролик.'
            : 'Або натисніть «написати сценарій заново» — агент зніме ролик за новою довідкою.'}
        </span>
      </div>
      {guide.kind === 'FAQ' && (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', marginTop: '0.4rem', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 20rem', color: 'var(--text)' }}>
            <SearchSelect items={free} value={slug} onChange={setSlug} placeholder="— питання довідки без ролика —"
              dropdownMinWidth={360} />
          </div>
          <button type="button" disabled={busy || !slug || relink.isPending} title={tip}
            onClick={() => relink.mutate()}>
            перепривʼязати
          </button>
        </div>
      )}
      {err && <div className="error">{err}</div>}
    </div>
  );
}

function Remarks({ guide, busy }: { guide: VideoGuide; busy: boolean }) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: () => platformVideo.remark(guide.id, text.trim()),
    onSuccess: () => {
      setText(''); setErr(null);
      qc.invalidateQueries({ queryKey: ['platform-video-guide', guide.id] });
      qc.invalidateQueries({ queryKey: ['platform-video-help'] });
    },
    onError: (e) => setErr(extractApiError(e)),
  });
  const blocked = busy ? 'Агент зараз працює над роликом — дочекайтесь'
    : !guide.script ? 'Дочекайтесь першої версії ролика' : undefined;
  return (
    <div>
      {guide.remarks.length === 0 && (
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: 0 }}>
          Зауважень ще не було. Напишіть звичайними словами, що змінити, — агент поправить сценарій
          (і темп мовлення, якщо про це просите) та перезніме ролик.
        </p>
      )}
      {guide.remarks.map((r) => (
        <div key={r.id} data-testid="video-remark" style={{ borderTop: '1px solid var(--border)', padding: '0.5rem 0' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
            {r.author_name || r.author_email || '—'} · {fmtDT(r.created_at)}
          </div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{r.text}</div>
          <div style={{ fontSize: '0.85rem', marginTop: '0.25rem',
            color: r.status === 'FAILED' ? 'var(--danger)' : 'var(--text-secondary)' }}>
            {r.status === 'NEW' && 'агент опрацьовує…'}
            {r.status === 'APPLIED' && <>агент: {r.agent_reply}</>}
            {r.status === 'FAILED' && <>не вдалося: {r.agent_reply || 'помилка агента'}</>}
          </div>
        </div>
      ))}
      <form onSubmit={(e) => { e.preventDefault(); send.mutate(); }} style={{ marginTop: '0.5rem' }}>
        <div className="field">
          <label htmlFor="vg-remark">Зауваження до ролика</label>
          <textarea id="vg-remark" rows={3} value={text} maxLength={4000} onChange={(e) => setText(e.target.value)}
            placeholder={REMARK_HINTS.join(' · ')} />
        </div>
        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', margin: '0 0 0.5rem' }}>
          {REMARK_HINTS.map((h) => (
            <button key={h} type="button" className="btn-link" style={{ fontSize: '0.8rem' }}
              onClick={() => setText((t) => (t ? `${t}\n${h}` : h))}>
              {h.toLowerCase()}
            </button>
          ))}
        </div>
        {err && <div className="error">{err}</div>}
        <button type="submit" className="primary" disabled={!!blocked || text.trim().length < 3 || send.isPending}
          title={blocked}>
          {send.isPending ? 'надсилаємо…' : 'надіслати агенту'}
        </button>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: '0.5rem 0 0' }}>
          Загальне із зауваження (як вимовляти слово, чого не повторювати, як натискати елемент) агент
          запамʼятовує для всіх роликів — див. <Link to="/platform/video?tab=memory">памʼять агента</Link>.
        </p>
      </form>
    </div>
  );
}

export default function PlatformVideoGuidePage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showRequest, setShowRequest] = useState(false);
  const canOpenArticle = useCanOpenHelpArticles();
  const q = useQuery({
    queryKey: ['platform-video-guide', id],
    queryFn: () => platformVideo.guide(id),
    refetchInterval: (query) => (busyOf(query.state.data) ? 5_000 : false),
  });
  const yt = useQuery({ queryKey: ['platform-video-youtube'], queryFn: platformVideo.youtube });
  const act = useMutation({
    mutationFn: (a: GuideAction) => platformVideo.action(id, a),
    onSuccess: (r, a) => {
      setErr(null);
      if (a === 'delete') { navigate('/platform/video', { replace: true }); return; }
      setMsg(a === 'approve'
        ? (r.result === 'PUBLISHING' ? 'Схвалено — ролик публікується на YouTube.' : 'Схвалено. Канал YouTube не підключено — ролик можна завантажити файлом.')
        : 'Поставлено в чергу — сторінка оновиться, коли агент закінчить.');
      qc.invalidateQueries({ queryKey: ['platform-video-guide', id] });
      qc.invalidateQueries({ queryKey: ['platform-video-help'] });
    },
    onError: (e) => { setMsg(null); setErr(extractApiError(e)); },
  });
  const g = q.data;
  if (q.isLoading) return <div className="card"><p>Завантаження…</p></div>;
  if (!g) return <div className="card"><div className="error">{extractApiError(q.error, 'Ролик не знайдено')}</div></div>;
  const busy = busyOf(g);
  const busyTip = busy ? 'Агент зараз працює над роликом — дочекайтесь' : undefined;
  const canApprove = ['REVIEW', 'APPROVED', 'OUTDATED', 'PUBLISHED'].includes(g.status) && g.has_video;
  const fileBase = (g.route_pattern ?? g.title ?? g.id).replace(/[/?=\s«»"']+/g, '_').slice(0, 80);
  // ч.1370: ролики до сторінок і за статтями — в одному списку «Довідка».
  const backTo = g.kind === 'TOPIC' ? '/platform/video?tab=topics' : '/platform/video';
  const topicArticle = g.kind === 'FAQ' && g.article?.kind === 'topic';
  const regenTip = g.kind === 'TOPIC' ? 'Агент пройде сценарій заново за завданням і перезніме'
    : topicArticle ? 'Агент заново пройде статтю довідки і перезніме'
      : g.kind === 'FAQ' ? 'Агент заново покаже відповідь на питання і перезніме'
        : 'Агент напише сценарій з нуля за статтею довідки і перезніме';

  return (
    <>
      <div className="card">
        <div className="toolbar" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
          <Link to={backTo}>← до списку</Link>
          <h2 style={{ margin: 0, flex: '1 1 18rem' }}>{g.title || g.route_pattern}</h2>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
            {topicArticle ? 'ролик за темою' : KIND_LABEL[g.kind]}</span>
          <VideoStatusBadge status={g.status} label={g.status_label} />
        </div>
        <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
          {g.kind === 'PAGE' && <>Сторінка /{g.route_pattern}</>}
          {g.kind === 'FAQ' && (topicArticle ? <>Ролик за статтею-темою довідки</> : <>Відповідь на питання</>)}
          {g.kind === 'TOPIC' && <>Сценарій через кілька сторінок</>}
          {g.article ? (canOpenArticle && g.article.id
            ? <> · стаття довідки <Link to={`/platform/support-help/${g.article.id}`}>«{g.article.title}»</Link></>
            : <> · стаття довідки «{g.article.title}»</>) : null}
          {g.visited_routes.length > 0 && (
            <span data-testid="video-visited"> · сторінки ролика: {g.visited_routes.map((r) => `/${r}`).join(', ')}</span>
          )}
          {' · '}тривалість {fmtDuration(g.duration_sec)} · версія {g.script_version}
          {g.rendered_at ? <> · знято {fmtDT(g.rendered_at)}</> : null}
          {g.youtube_url ? <> · <a href={g.youtube_url} target="_blank" rel="noreferrer">на YouTube</a></> : null}
        </p>
        <HelpChanged guide={g} busy={busy} onDone={(m) => { setErr(null); setMsg(m); }} />
        {g.last_error && (
          <div style={{ background: 'var(--badge-amber-bg)', color: 'var(--badge-amber-fg)',
            border: '1px solid var(--badge-amber-bd)', borderRadius: 'var(--radius-sm)', padding: '0.5rem 0.75rem',
            fontSize: '0.875rem', marginBottom: '0.75rem', whiteSpace: 'pre-wrap' }}>
            {g.last_error}
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 3fr) minmax(16rem, 2fr)', gap: '1rem' }}
          className="video-guide-grid">
          <div>
            <DemoModeBlock guide={g} busy={busy} onSaved={(m) => { setErr(null); setMsg(m); }} />
            {(g.kind === 'TOPIC' || g.kind === 'PAGE') && (
              <BriefBlock guide={g} busy={busy} onSaved={(m) => { setErr(null); setMsg(m); }} />
            )}
            {g.kind === 'PAGE' && g.coverage && <CoverageBlock coverage={g.coverage} />}
            <Player guide={g} />
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
              <button type="button" className="primary" disabled={!canApprove || busy || act.isPending}
                title={busyTip ?? (canApprove ? undefined : 'Схвалити можна готовий ролик')}
                onClick={() => act.mutate('approve')}>
                {yt.data?.connected ? 'схвалити і опублікувати' : 'схвалити'}
              </button>
              <button type="button" disabled={!g.script || busy || act.isPending}
                title={busyTip ?? 'Зняти ще раз з тим самим сценарієм — наприклад, після зміни налаштувань голосу'}
                onClick={() => act.mutate('rerender')}>
                перезняти
              </button>
              <button type="button" disabled={busy || act.isPending}
                title={busyTip ?? regenTip}
                onClick={() => { if (window.confirm('Написати сценарій заново? Поточний сценарій буде замінено.')) act.mutate('regenerate'); }}>
                написати сценарій заново
              </button>
              <button type="button" disabled={!g.has_video} title={g.has_video ? undefined : 'Ролика ще немає'}
                onClick={() => download(g.id, 'mp4', `${fileBase}.mp4`).catch((e) => setErr(extractApiError(e)))}>
                завантажити відео
              </button>
              <button type="button" disabled={!g.has_video} title={g.has_video ? undefined : 'Ролика ще немає'}
                onClick={() => download(g.id, 'srt', `${fileBase}.srt`).catch((e) => setErr(extractApiError(e)))}>
                субтитри
              </button>
              <button type="button" onClick={() => setShowRequest((v) => !v)}>запит розробнику</button>
              <button type="button" className="danger" disabled={busy || act.isPending} title={busyTip}
                onClick={() => { if (window.confirm('Видалити ролик? Файли буде стерто, на YouTube він лишиться.')) act.mutate('delete'); }}>
                видалити
              </button>
            </div>
            {msg && <div role="status" style={NOTICE}>{msg}</div>}
            {err && <div className="error">{err}</div>}
            {showRequest && (
              <div style={{ marginTop: '0.75rem' }}>
                <DevRequestForm guideId={g.id} onSent={() => setShowRequest(false)} />
              </div>
            )}
          </div>
          <div>
            <h3 style={{ marginTop: 0 }}>Зауваження агенту</h3>
            <Remarks guide={g} busy={busy} />
          </div>
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Сценарій</h3>
        <ScriptBlock guide={g} busy={busy} />
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Що робив агент</h3>
        <TchTable style={{ width: '100%' }}>
          <thead>
            <tr><th>Коли</th><th>Робота</th><th>Стан</th><th>Примітка</th></tr>
          </thead>
          <tbody>
            {g.jobs.map((j) => (
              <tr key={j.id}>
                <td style={{ width: '11rem' }}>{fmtDT(j.created_at)}</td>
                <td style={{ width: '11rem' }}>{JOB_LABEL[j.kind] ?? j.kind}</td>
                <td style={{ width: '8rem' }}>{JOB_STATUS[j.status] ?? j.status}</td>
                <td style={{ fontSize: '0.8rem' }}>{j.last_error ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </TchTable>
      </div>
    </>
  );
}
